
import { AppState } from '@/types';
import apiService from '@/lib/apiService'; // Import the true HTTP client
import { useAuthStore } from '@/store/authStore';

/**
 * Saves the entire application state to the backend for the 'birchwood' family.
 * This service uses the centralized apiService to handle the actual POST request.
 *
 * @param state The complete application state to be saved.
 */
export const saveStateToBackend = async (state: AppState): Promise<void> => {
  try {
    // The apiService will automatically add the Authorization header.
    await apiService.post('/api/family/birchwood/save', state);
  } catch (error) {
    console.error('Failed to save state to backend:', error);
    // Optionally, re-throw or handle the error for the UI.
    throw error;
  }
};

/**
 * Loads the application state from the backend.
 * This function implements the required two-step authentication flow:
 * 1. Verify the user's ID token with the backend's authentication endpoint.
 * 2. If successful, proceed to fetch the family's save data.
 *
 * @returns A promise that resolves to the AppState, or null if no save data is found.
 */
export const loadStateFromBackend = async (): Promise<AppState | null> => {
  try {
    const idToken = useAuthStore.getState().userToken;
    if (!idToken) {
      throw new Error('User is not authenticated locally.');
    }

    // Step 1: Authenticate with the backend. apiService handles this POST request.
    await apiService.post('/api/auth/authenticate', { idToken });

    // Step 2: If authentication succeeds, load the family data.
    // The apiService will automatically add the Authorization header for this GET request.
    const state = await apiService.get<AppState>('/api/family/birchwood/load');
    return state;

  } catch (error: any) {
    // The backend can return a 404 if no save file exists, which is not an error condition.
    // We check for this to return null, allowing the app to create a new default state.
    if (error.message && error.message.toLowerCase().includes('not found')) {
      return null;
    }
    
    console.error('Failed to load state from backend:', error);
    throw error; // Re-throw other errors to be handled by the caller.
  }
};
